// TopMenu.jsx
import React, { useState, useEffect, useContext } from "react";
import {
    AppBar,
    Toolbar,
    IconButton,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Box,
    Typography,
    Button,
    Divider,
    Menu,
    MenuItem,
    Chip,
    useMediaQuery,
    useScrollTrigger,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import {
    Upload,
    List as ListIcon,
    Analytics,
    AdminPanelSettings,
    PersonAdd,
    KeyboardArrowDownRounded,
    LogoutRounded,
    SettingsRounded,
    CheckCircleRounded,
    DoNotDisturbOnRounded,
    BusinessRounded,
    TuneRounded,
    AddCircleRounded,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import LogoutButton from "../Logout";
import { UserRoleContext } from "../GetUserRole";
import axios from "axios";
import { API_URL } from "../config";
import { getSharedNavAppBarSx, NAVBAR_HEIGHT, QR_FLEX_WORDMARK_SX, sharedNavToolbarSx } from "./navTheme";

const Offset = () => <Box sx={{ height: NAVBAR_HEIGHT }} />;

export default function TopMenu() {
    const isMobile = useMediaQuery("(max-width:960px)");
    const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 6 });
    const location = useLocation();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [adminMenuEl, setAdminMenuEl] = useState(null);

    const { role } = useContext(UserRoleContext);
    const [plan, setPlan] = useState("");

    useEffect(() => {
        axios
            .get(API_URL + "get_plan.php", { withCredentials: true })
            .then((res) => setPlan(res.data))
            .catch(() => { });
    }, []);

    const navItems = [
        ...(role < 4 ? [{ name: "Create", path: "/upload", icon: <Upload /> }] : []),
        { name: "My List", path: "/list", icon: <ListIcon /> },
        { name: "Analytics", path: "/analytics", icon: <Analytics /> },
        { name: "Referral", path: "/referral", icon: <PersonAdd /> },
    ];

    const adminItems = [
        ...(role < 4
            ? [
                {
                    name: "Create User",
                    path: "/create-user",
                    icon: <AddCircleRounded fontSize="small" />,
                    description: "Add a new team member account.",
                },
                {
                    name: "Change Plan",
                    path: "/change-plan",
                    icon: <TuneRounded fontSize="small" />,
                    description: "Update billing and workspace limits.",
                },
            ]
            : []),
        ...(role === 1
            ? [
                {
                    name: "Approve Business",
                    path: "/approve",
                    icon: <CheckCircleRounded fontSize="small" />,
                    description: "Review and activate new businesses.",
                },
                {
                    name: "Deactivate Business",
                    path: "/deactivate",
                    icon: <DoNotDisturbOnRounded fontSize="small" />,
                    description: "Pause businesses that need attention.",
                },
                {
                    name: "Manage Businesses",
                    path: "/businesses",
                    icon: <BusinessRounded fontSize="small" />,
                    description: "View and manage all business accounts.",
                },
            ]
            : []),
    ];

    const toggleDrawer = () => setDrawerOpen((o) => !o);
    const openAdminMenu = (e) => setAdminMenuEl(e.currentTarget);
    const closeAdminMenu = () => setAdminMenuEl(null);

    const getRoleLabel = () => {
        if (role === 1) return "Super Admin";
        if (role === 2) return "Owner";
        if (role === 3) return "Admin";
        return "Team Member";
    };

    const getNavSecondaryText = (itemName) => {
        switch (itemName) {
            case "Create":
                return "Create fresh QR records for files, URLs, websites, and surveys.";
            case "My List":
                return "Review and manage your saved records.";
            case "Analytics":
                return "Track scans, views, and customer activity.";
            default:
                return "Invite others and grow your reach.";
        }
    };

    const mobileNavItemSx = (active = false) => ({
        mb: 1.1,
        borderRadius: 3.5,
        px: 1.25,
        py: 1.15,
        border: active ? "1px solid rgba(79, 70, 229, 0.24)" : "1px solid rgba(148, 163, 184, 0.14)",
        background: active
            ? "linear-gradient(135deg, rgba(79,70,229,0.14) 0%, rgba(6,182,212,0.1) 100%)"
            : "linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.96) 100%)",
        boxShadow: active ? "0 16px 32px rgba(79, 70, 229, 0.12)" : "0 8px 18px rgba(15, 23, 42, 0.05)",
        alignItems: "flex-start",
        transition: "all .18s ease",
        "& .MuiListItemIcon-root": {
            minWidth: 42,
            mt: 0.1,
            color: active ? "#4338ca" : "#334155",
        },
        "& .MuiListItemText-primary": {
            fontWeight: 700,
            color: "#0f172a",
        },
        "& .MuiListItemText-secondary": {
            color: "#64748b",
            mt: 0.35,
            lineHeight: 1.45,
        },
        "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: "0 18px 34px rgba(79, 70, 229, 0.12)",
            background: "linear-gradient(135deg, rgba(79,70,229,0.12) 0%, rgba(6,182,212,0.08) 100%)",
        },
    });

    const adminButtonSx = {
        textTransform: "none",
        borderRadius: 999,
        borderColor: "rgba(95, 76, 255, 0.32)",
        color: "#312e81",
        fontWeight: 800,
        letterSpacing: "0.01em",
        px: 2.2,
        minHeight: 46,
        background:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(238, 242, 255, 0.92) 100%)",
        boxShadow: "0 10px 24px rgba(79, 70, 229, 0.14)",
        transition: "all .2s ease",
        "& .MuiButton-startIcon, & .MuiButton-endIcon": {
            color: "#5b5df0",
        },
        "&:hover": {
            borderColor: "rgba(79, 70, 229, 0.5)",
            bgcolor: "rgba(255, 255, 255, 0.96)",
            transform: "translateY(-1px)",
            boxShadow: "0 16px 32px rgba(79, 70, 229, 0.18)",
        },
    };

    const adminMenuPaperSx = {
        mt: 1.75,
        width: { xs: 260, sm: 300 },
        borderRadius: 4,
        overflow: "visible",
        border: "1px solid rgba(99, 102, 241, 0.14)",
        background:
            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(244,247,255,0.98) 100%)",
        backdropFilter: "blur(18px)",
        boxShadow: "0 28px 60px rgba(15, 23, 42, 0.18)",
        p: 0.75,
        "&::before": {
            content: '""',
            position: "absolute",
            top: -8,
            right: 34,
            width: 16,
            height: 16,
            background: "rgba(252, 252, 255, 0.99)",
            borderTop: "1px solid rgba(99, 102, 241, 0.14)",
            borderLeft: "1px solid rgba(99, 102, 241, 0.14)",
            transform: "rotate(45deg)",
            zIndex: 0,
        },
    };

    const adminMenuItemSx = {
        alignItems: "flex-start",
        gap: 1.4,
        borderRadius: 3,
        mx: 0,
        my: 0.35,
        px: 1.2,
        py: 1.05,
        minHeight: 58,
        color: "text.primary",
        transition: "all .18s ease",
        "&:hover": {
            bgcolor: "rgba(79, 70, 229, 0.08)",
            transform: "translateY(-1px)",
            "& .admin-menu-icon": {
                bgcolor: "rgba(79, 70, 229, 0.16)",
                color: "#4338ca",
            },
        },
    };

    /** Drawer (mobile) */
    const drawerContent = (
        <Box
            role="navigation"
            sx={{
                width: 326,
                maxWidth: "100vw",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                background: "linear-gradient(180deg, #f8fbff 0%, #f8f7ff 55%, #ffffff 100%)",
            }}
            onKeyDown={(e) => e.key === "Escape" && toggleDrawer()}
        >
            <Box
                sx={{
                    px: 2,
                    pt: 2,
                    pb: 1.5,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backdropFilter: "blur(12px)",
                    background: "linear-gradient(180deg, rgba(248,251,255,0.94) 0%, rgba(248,247,255,0.88) 100%)",
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 4,
                        color: "#0f172a",
                        border: "1px solid rgba(99, 102, 241, 0.14)",
                        background: "linear-gradient(135deg, rgba(79,70,229,0.16) 0%, rgba(6,182,212,0.12) 100%)",
                        boxShadow: "0 20px 36px rgba(79, 70, 229, 0.12)",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.25 }}>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h6"
                                component={Link}
                                to="/"
                                onClick={toggleDrawer}
                                sx={{
                                    textDecoration: "none",
                                    fontWeight: 900,
                                    display: "inline-block",
                                    background: "linear-gradient(110deg, #312e81 0%, #6d28d9 55%, #0891b2 100%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}
                            >
                                QR Flex
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.7, color: "rgba(15, 23, 42, 0.72)", lineHeight: 1.45 }}>
                                Signed in as {getRoleLabel()}.
                            </Typography>
                        </Box>
                        <Chip
                            label={plan ? `Plan: ${plan}` : getRoleLabel()}
                            size="small"
                            sx={{
                                alignSelf: "flex-start",
                                bgcolor: "rgba(255,255,255,0.72)",
                                fontWeight: 700,
                                color: "#312e81",
                                border: "1px solid rgba(99, 102, 241, 0.16)",
                            }}
                        />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", px: 2, pb: 2 }}>
                <Typography variant="overline" sx={{ px: 0.75, color: "#6366f1", fontWeight: 800, letterSpacing: "0.14em" }}>
                    Workspace
                </Typography>
                <List disablePadding sx={{ pt: 0.6, pb: adminItems.length ? 1.25 : 0 }}>
                    {navItems.map((item) => {
                        const active = location.pathname === item.path;
                        return (
                            <ListItemButton
                                key={item.name}
                                component={Link}
                                to={item.path}
                                onClick={toggleDrawer}
                                sx={mobileNavItemSx(active)}
                            >
                                <ListItemIcon>{item.icon}</ListItemIcon>
                                <ListItemText primary={item.name} secondary={getNavSecondaryText(item.name)} />
                            </ListItemButton>
                        );
                    })}
                </List>

                {adminItems.length > 0 && (
                    <>
                        <Divider sx={{ my: 1.25, borderColor: "rgba(148, 163, 184, 0.16)" }} />
                        <Typography variant="overline" sx={{ px: 0.75, color: "#6366f1", fontWeight: 800, letterSpacing: "0.14em" }}>
                            Admin tools
                        </Typography>
                        <List disablePadding sx={{ pt: 0.6 }}>
                            {adminItems.map((item) => {
                                const active = location.pathname === item.path;
                                return (
                                    <ListItemButton
                                        key={item.name}
                                        component={Link}
                                        to={item.path}
                                        onClick={toggleDrawer}
                                        sx={mobileNavItemSx(active)}
                                    >
                                        <ListItemIcon>{item.icon}</ListItemIcon>
                                        <ListItemText primary={item.name} secondary={item.description} />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </>
                )}
            </Box>

            <Box
                sx={{
                    mt: "auto",
                    p: 2,
                    borderTop: "1px solid rgba(148, 163, 184, 0.16)",
                    background: "rgba(255,255,255,0.76)",
                    backdropFilter: "blur(10px)",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.2,
                        px: 1.35,
                        py: 1.25,
                        borderRadius: 3.5,
                        color: "#475569",
                        border: "1px solid rgba(248, 113, 113, 0.18)",
                        background: "linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(254,242,242,0.9) 100%)",
                        boxShadow: "0 10px 24px rgba(239, 68, 68, 0.08)",
                    }}
                >
                    <LogoutRounded sx={{ fontSize: 20, color: "#dc2626" }} />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", color: "#ef4444" }}>
                            Session
                        </Typography>
                        <LogoutButton sx={{ fontWeight: 800, color: "#991b1b", cursor: "pointer" }} />
                    </Box>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box component="header" sx={{ mb: { md: 0 } }}>
            <AppBar
                position="fixed"
                color="transparent"
                elevation={scrolled ? 3 : 0}
                sx={{
                    ...getSharedNavAppBarSx(scrolled),
                    transition: "all .25s ease",
                }}
            >
                <Toolbar
                    sx={{
                        ...sharedNavToolbarSx,
                        maxWidth: 1200,
                        px: { xs: 1.5, md: 2 },
                    }}
                >
                    {/* Left: menu icon (mobile) */}
                    {isMobile && (
                        <IconButton
                            edge="start"
                            onClick={toggleDrawer}
                            aria-label="Open menu"
                            size="large"
                            sx={{ color: "text.primary" }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    {/* Brand — centered on mobile, left on desktop */}
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        component={Link}
                        to="/"
                        sx={{
                            ...QR_FLEX_WORDMARK_SX,
                            position: isMobile ? "absolute" : "relative",
                            left: isMobile ? "50%" : "auto",
                            transform: isMobile ? "translateX(-50%)" : "none",
                            letterSpacing: 0.3,
                        }}
                    >
                        QR Flex
                    </Typography>

                    {/* Desktop nav */}
                    {!isMobile && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, ml: "auto" }}>
                            {plan && (
                                <Chip
                                    label={`Plan: ${plan}`}
                                    size="small"
                                    sx={{ bgcolor: "action.hover", fontWeight: 600 }}
                                />
                            )}

                            {navItems.map((item) => (
                                <Button
                                    key={item.name}
                                    component={Link}
                                    to={item.path}
                                    startIcon={item.icon}
                                    sx={{
                                        color: "text.primary",
                                        textTransform: "none",
                                        fontWeight: 600,
                                        "&:hover": { color: "primary.main", bgcolor: "action.hover" },
                                    }}
                                >
                                    {item.name}
                                </Button>
                            ))}

                            {adminItems.length > 0 && (
                                <>
                                    <Button
                                        onClick={openAdminMenu}
                                        variant="outlined"
                                        startIcon={<AdminPanelSettings />}
                                        endIcon={<KeyboardArrowDownRounded />}
                                        sx={adminButtonSx}
                                    >
                                        Admin
                                    </Button>
                                    <Menu
                                        anchorEl={adminMenuEl}
                                        open={Boolean(adminMenuEl)}
                                        onClose={closeAdminMenu}
                                        transformOrigin={{ horizontal: "right", vertical: "top" }}
                                        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                                        slotProps={{
                                            paper: {
                                                sx: adminMenuPaperSx,
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                px: 1.25,
                                                pt: 1,
                                                pb: 0.75,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <Box>
                                                <Typography variant="overline" sx={{ color: "#5b5df0", fontWeight: 800, letterSpacing: "0.14em", lineHeight: 1.2 }}>
                                                    Admin tools
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.35 }}>
                                                    Quick access to workspace management.
                                                </Typography>
                                            </Box>
                                            <SettingsRounded sx={{ color: "#5b5df0", fontSize: 20 }} />
                                        </Box>
                                        <Divider sx={{ my: 0.5, borderColor: "rgba(15, 23, 42, 0.08)" }} />
                                        {adminItems.map((item) => (
                                            <MenuItem
                                                key={item.name}
                                                component={Link}
                                                to={item.path}
                                                onClick={closeAdminMenu}
                                                sx={adminMenuItemSx}
                                            >
                                                <Box
                                                    className="admin-menu-icon"
                                                    sx={{
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius: 2.5,
                                                        display: "grid",
                                                        placeItems: "center",
                                                        bgcolor: "rgba(79, 70, 229, 0.1)",
                                                        color: "#4f46e5",
                                                        flexShrink: 0,
                                                        transition: "all .18s ease",
                                                    }}
                                                >
                                                    {item.icon}
                                                </Box>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.25 }}>
                                                        {item.name}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.4, whiteSpace: "normal", lineHeight: 1.45 }}>
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                        <Divider sx={{ my: 0.75, borderColor: "rgba(15, 23, 42, 0.08)" }} />
                                        <Box
                                            sx={{
                                                px: 1.1,
                                                pb: 0.35,
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1.2,
                                                borderRadius: 3,
                                                py: 1.15,
                                                mx: 0.1,
                                                color: "#475569",
                                                backgroundColor: "rgba(15, 23, 42, 0.04)",
                                                transition: "all .18s ease",
                                                "&:hover": {
                                                    backgroundColor: "rgba(239, 68, 68, 0.08)",
                                                    color: "#b91c1c",
                                                },
                                                "& .logout-button": {
                                                    fontWeight: 700,
                                                    cursor: "pointer",
                                                },
                                            }}
                                        >
                                            <LogoutRounded sx={{ fontSize: 20 }} />
                                            <Box onClick={closeAdminMenu} sx={{ width: "100%" }}>
                                                <LogoutButton className="logout-button" />
                                            </Box>
                                        </Box>
                                    </Menu>
                                </>
                            )}
                        </Box>
                    )}

                    {/* Right spacer (mobile) to balance centered brand when menu icon is visible */}
                    {isMobile && <Box sx={{ width: 40, height: 1 }} />}
                </Toolbar>
            </AppBar>

            <Offset />

            {/* Mobile Drawer */}
            {isMobile && (
                <Drawer
                    anchor="left"
                    open={drawerOpen}
                    onClose={toggleDrawer}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{
                        sx: {
                            bgcolor: "transparent",
                            backgroundImage: "none",
                            boxShadow: "0 28px 60px rgba(15, 23, 42, 0.22)",
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
        </Box>
    );
}
